import { createSignal } from './signal';
/**
 * Emits the latest values when any source changes. Trace records identify the
 * sources rather than repeating their payloads, which each source already logs.
 * Observer delivery retains the complete values and their identities.
 *
 * @example
 * const latest = combine(audioStats, videoStats);
 */
export const combine = (...signals) => {
    const latest = Array.from(signals, _ => undefined);
    let detachCombinedSignals = [];
    const attach = () => {
        detachCombinedSignals = signals.map((signal, i) => signal.add(value => {
            latest[i] = value;
            combinedSignal.emit(latest);
        }));
    };
    const detach = () => {
        for (const detach of detachCombinedSignals) {
            detach();
        }
    };
    const combinedSignal = createSignal({
        describeSubject: () => ({ sources: signals.map(signal => signal.name) }),
    });
    const combinedSignalProxy = new Proxy(combinedSignal, {
        get: (target, p, args) => {
            switch (p) {
                case 'add':
                    return (...args) => {
                        const value = target[p](...args);
                        if (detachCombinedSignals.length === 0 &&
                            combinedSignal.size > 0) {
                            attach();
                        }
                        return value;
                    };
                case 'remove':
                    return (...args) => {
                        const value = target[p](...args);
                        if (combinedSignal.size === 0) {
                            detach();
                        }
                        return value;
                    };
                default:
                    return Reflect.get(target, p, args);
            }
        },
    });
    return combinedSignalProxy;
};
//# sourceMappingURL=combine.js.map